import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed } from 'vue'

import { callPjazzaAccount } from '@/domains/pjazza/services'
import { useBasketStore } from '@/domains/pjazza/store/use-basket-store/useBasketStore.ts'
import { useLiveOrdersStore } from '@/domains/pjazza/store/use-live-orders-store/useLiveOrdersStore.ts'
import { useTabStore } from '@/domains/pjazza/store/use-tab-store/useTabStore.ts'

/** What a caller knows about the diner at the moment they sign in. */
interface PjazzaSession {
  token: string
  expiresAt: string | null
  phone?: string | null
  name?: string | null
  avatarUrl?: string | null
}

/**
 * The diner's session, and the little of them the chrome has to draw.
 *
 * Passwordless: the token minted by phone verification is the whole
 * credential, so "signed in" simply means holding an unexpired one. Stored
 * under `auth_token` because that is the key the shared ApiClient reads when
 * a request opts into auth.
 *
 * The name, number and photo are kept here for one reason: the tab bar is on
 * every screen in the Pjazza context, including Places, which fetches no
 * account at all. Without them the account menu would either show a stranger
 * or have to pull a whole order history to put a face on a 24px button.
 * They are a label, never the identity — the token is that.
 */
export const usePjazzaAuthStore = defineStore('pjazza-auth', () => {
  /**
   * flush: 'sync' throughout, and it is load-bearing rather than tidiness.
   *
   * The shared ApiClient reads `auth_token` out of localStorage when a request
   * opts into auth — it does not read this store. useLocalStorage defaults to
   * 'pre', which queues the write on Vue's scheduler, so the key was still the
   * old value for the rest of the tick. Both sign-in paths request something
   * immediately afterwards, in that same tick:
   *
   *   - PjSignIn signs in and emits, and You/Show loads the order history
   *   - useCheckoutFlow signs in and places the order
   *
   * Both therefore went out with no Authorization header at all. The account
   * screen took the resulting 401 as a revoked token and signed the diner
   * straight back out, one frame after they signed in.
   */
  const SYNC = { flush: 'sync', onError: () => undefined } as const

  const token = useLocalStorage<string | null>('auth_token', null, SYNC)
  const expiresAt = useLocalStorage<string | null>('auth_token_expires_at', null, SYNC)

  const storedPhone = useLocalStorage<string | null>('pjazza_phone', null, SYNC)
  const storedName = useLocalStorage<string | null>('pjazza_name', null, SYNC)
  const storedAvatarUrl = useLocalStorage<string | null>('pjazza_avatar_url', null, SYNC)

  const isSignedIn = computed(() => {
    if (!token.value) return false
    if (!expiresAt.value) return true

    return new Date(expiresAt.value).getTime() > Date.now()
  })

  /*
   * The label is read through isSignedIn, never straight off the device.
   *
   * A token expires on its own after a day, so isSignedIn goes false with no
   * code running and no request made. The three profile keys stay in
   * localStorage. Handing those refs out raw meant every consumer had to
   * remember the gate, and the ones that never call the API — the header on
   * every screen, the account menu, the account sheet — had no reason to: a
   * signed-out diner read their own first name in the chrome, and their phone
   * number in the menu.
   *
   * Computed here rather than checked at each call site, because a fourth
   * screen drawing a name must not be able to get this wrong.
   */
  const phone = computed(() => (isSignedIn.value ? storedPhone.value : null))
  const name = computed(() => (isSignedIn.value ? storedName.value : null))
  const avatarUrl = computed(() => (isSignedIn.value ? storedAvatarUrl.value : null))

  /**
   * What the account menu draws when there is no photo. Two letters from a
   * name, one from a first name, and nothing at all before somebody has told
   * us who they are — a monogram of a phone number means nothing.
   */
  const initials = computed(() => {
    const parts = (name.value ?? '').trim().split(/\s+/).filter(Boolean)

    if (parts.length === 0) return null

    return parts
      .slice(0, 2)
      .map(part => [...part][0]?.toUpperCase() ?? '')
      .join('')
  })

  const signIn = (session: PjazzaSession): void => {
    token.value = session.token
    expiresAt.value = session.expiresAt

    // Only what the caller actually knows. Checkout verifies without ever
    // reading the account, so it passes a number and nothing else — and must
    // not blank a name that is already on file.
    if (session.phone !== undefined && session.phone !== null) storedPhone.value = session.phone
    if (session.name !== undefined) storedName.value = session.name
    if (session.avatarUrl !== undefined) storedAvatarUrl.value = session.avatarUrl
  }

  /** Kept in step when the account screen changes either one. */
  const setProfile = (profile: { name?: string | null; avatarUrl?: string | null }): void => {
    if (profile.name !== undefined) storedName.value = profile.name
    if (profile.avatarUrl !== undefined) storedAvatarUrl.value = profile.avatarUrl
  }

  /** Everything this store keeps on the device, and nothing else's. */
  const forgetDevice = (): void => {
    token.value = null
    expiresAt.value = null
    storedPhone.value = null
    storedName.value = null
    storedAvatarUrl.value = null
  }

  /*
   * A dead session leaves the diner's name and number on the device, and
   * nothing was ever going to come and collect them.
   *
   * Two ways in, and neither runs a line of our code. A token expires when a
   * timestamp passes. And the shared ApiClient removes `auth_token` itself on
   * any 401 from an authenticated request — see apiClient.ts — which it does
   * from a background tab poll, with no handler of ours involved. It knows
   * nothing of the three `pjazza_*` keys, because they are ours alone.
   *
   * So the test is "signed out with something still on file", not "expired".
   * Keying it off the token would have missed the 401 path entirely: by then
   * the token is already gone and only the name is left. The computed above
   * stops it being drawn; this stops it being kept.
   *
   * Only touches this store's own keys. The tab and basket are cleared by
   * signOut, which is a deliberate act with a caller — reaching into other
   * stores while this one is still being constructed is not.
   */
  const hasProfileOnFile = (): boolean =>
    storedName.value !== null || storedPhone.value !== null || storedAvatarUrl.value !== null

  if (!isSignedIn.value && hasProfileOnFile()) forgetDevice()

  const signOut = (): void => {
    /*
     * Tell the server first, because the request needs the token this is about
     * to drop — the ApiClient reads `auth_token` out of localStorage, so
     * clearing before firing would send the revoke unauthenticated.
     *
     * Not awaited, and its failure is swallowed on purpose. Signing out has to
     * work on a dead restaurant wifi, and the device is signed out either way;
     * the token then lapses on its own within the day. Here rather than at each
     * button, so a third sign-out cannot forget it.
     */
    if (token.value)
      void callPjazzaAccount()
        .signOut()
        .catch(() => undefined)

    forgetDevice()

    // Leaving the account leaves the table with it. A tab is a shared bill a
    // diner orders onto as themselves — every round has to be attributable to a
    // person — so a signed-out device holding a table it can no longer order to
    // is a dead end. Dropping it here covers every sign-out button at once.
    useTabStore().leave()
    useLiveOrdersStore().clear()
    useBasketStore().clear()
    useBasketStore().cancelPending()
  }

  return {
    token,
    expiresAt,
    phone,
    name,
    avatarUrl,
    initials,
    isSignedIn,
    signIn,
    setProfile,
    signOut
  }
})

export type { PjazzaSession }
